import * as React from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox, EmptyState, Skeleton } from "@/components/ui/primitives";

/**
 * Table used by every list screen.
 *
 * Uses real table semantics — <table>, <caption>, scope on headers, aria-sort on
 * sortable columns — rather than a grid of divs, so screen readers can navigate
 * it and announce row and column context.
 */

export interface Column<T> {
  /** Stable key; also the sort field sent to the API when `sortable`. */
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  className?: string;
  /** Hide below the md breakpoint. */
  hideOnMobile?: boolean;
  /** Excluded from the accessible-summary description. */
  srSkip?: boolean;
}

export interface DataTableProps<T> {
  /** Accessible description of the table's contents. Required. */
  caption: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Skeleton row count while loading. */
  skeletonRows?: number;

  sort?: string;
  order?: "asc" | "desc";
  onSortChange?: (field: string, order: "asc" | "desc") => void;

  onRowClick?: (row: T) => void;

  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;

  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;

  total?: number;
  nextCursor?: string | null;
  onLoadMore?: () => void;
  loadingMore?: boolean;

  className?: string;
}

const alignClass = (align?: "left" | "right" | "center") =>
  align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  loading,
  skeletonRows = 8,
  sort,
  order = "desc",
  onSortChange,
  onRowClick,
  selectable,
  selectedIds = [],
  onSelectionChange,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  emptyAction,
  total,
  nextCursor,
  onLoadMore,
  loadingMore,
  className,
}: DataTableProps<T>) {
  const allIds = React.useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    // Selecting "all" means all *loaded* rows, not every row matching the
    // filter. Bulk actions take explicit ids, so this stays unambiguous.
    onSelectionChange(allSelected ? [] : allIds);
  };

  const toggleOne = (id: string) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id],
    );
  };

  const handleSort = (field: string) => {
    if (!onSortChange) return;
    onSortChange(field, sort === field && order === "desc" ? "asc" : "desc");
  };

  const ariaSort = (field: string): "ascending" | "descending" | "none" =>
    sort !== field ? "none" : order === "asc" ? "ascending" : "descending";

  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            {caption}
            {total != null && `. ${total} total.`}
          </caption>

          <thead>
            <tr className="border-b border-border bg-surface/60">
              {selectable && (
                <th scope="col" className="w-10 px-3 py-2.5">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onCheckedChange={toggleAll}
                    aria-label={
                      allSelected ? "Clear selection" : "Select all loaded rows"
                    }
                  />
                </th>
              )}

              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={col.sortable ? ariaSort(col.key) : undefined}
                  className={cn(
                    "whitespace-nowrap px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                    alignClass(col.align),
                    col.hideOnMobile && "hidden md:table-cell",
                    col.className,
                  )}
                >
                  {col.sortable && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded transition-colors hover:text-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        col.align === "right" && "flex-row-reverse",
                      )}
                    >
                      {col.header}
                      {sort === col.key ? (
                        order === "asc" ? (
                          <ArrowUp className="h-3 w-3" aria-hidden />
                        ) : (
                          <ArrowDown className="h-3 w-3" aria-hidden />
                        )
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }).map((_, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    {selectable && (
                      <td className="px-3 py-3">
                        <Skeleton className="h-4 w-4" />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-3 py-3",
                          col.hideOnMobile && "hidden md:table-cell",
                        )}
                      >
                        <Skeleton className="h-4 w-full max-w-[140px]" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => {
                  const id = rowKey(row);
                  const selected = selectedIds.includes(id);
                  return (
                    <tr
                      key={id}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn(
                        "border-b border-border/60 transition-colors last:border-0",
                        onRowClick && "cursor-pointer hover:bg-surface/50",
                        selected && "bg-primary/[0.06]",
                      )}
                    >
                      {selectable && (
                        <td
                          className="px-3 py-3"
                          // Clicking the checkbox must not also open the row.
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => toggleOne(id)}
                            aria-label={`Select row ${id}`}
                          />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            "px-3 py-3 align-middle",
                            alignClass(col.align),
                            col.hideOnMobile && "hidden md:table-cell",
                            col.className,
                          )}
                        >
                          {col.render(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {loading
            ? "Loading…"
            : total != null
              ? `Showing ${rows.length} of ${total.toLocaleString("en-IN")}`
              : `${rows.length} row${rows.length === 1 ? "" : "s"}`}
        </p>

        {nextCursor && onLoadMore && (
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            loading={loadingMore}
          >
            Load more
          </Button>
        )}
      </div>
    </div>
  );
}
